"use client";

import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useUsageBuckets } from "@/lib/usage/usage.hooks";

const chartConfig = {
  spend: {
    label: "Spend",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

const monthLabelFormatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
const dayLabelFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
/** Lighthouse DateTime scalar expects `Y-m-d H:i:s` (not date-only ISO). */
const toDateTime = (d: Date, endOfDay = false) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day} ${endOfDay ? "23:59:59" : "00:00:00"}`;
};

export const DashboardChartSpend = () => {
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));

  const from = useMemo(() => toDateTime(startOfMonth(monthCursor)), [monthCursor]);
  const to = useMemo(() => toDateTime(endOfMonth(monthCursor), true), [monthCursor]);

  const { data, isLoading } = useUsageBuckets({ from, to, interval: "DAY" });
  const buckets = useMemo(() => data?.usage_buckets ?? [], [data]);

  const daily = useMemo(
    () =>
      buckets.map((b) => ({
        date: dayLabelFormatter.format(new Date(`${b.date}T00:00:00`)),
        spend: Number(b.cost_cents) / 100,
      })),
    [buckets],
  );

  const shiftMonth = (delta: number) => {
    setMonthCursor((prev) => startOfMonth(new Date(prev.getFullYear(), prev.getMonth() + delta, 1)));
  };

  return (
    <Card className="gap-4 py-5">
      <div className="flex flex-col gap-4 px-5 sm:flex-row sm:items-start">
        <div className="space-y-1">
          <CardTitle>Daily spend</CardTitle>
          <CardDescription>Spend per day from your usage history.</CardDescription>
        </div>
        <div className="flex flex-col gap-4 sm:ml-auto sm:flex-row sm:items-center">
          <div className="inline-flex items-center gap-2 self-center rounded-lg bg-gray-100 p-1 sm:self-auto">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <IconChevronLeft className="size-4" />
            </Button>
            <span className="min-w-16 text-center text-sm">{monthLabelFormatter.format(monthCursor)}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              <IconChevronRight className="size-4" />
            </Button>
          </div>
        </div>
        <Button variant="outline" size="sm" className="self-start sm:self-auto" asChild>
          <Link href="/usage">View usage</Link>
        </Button>
      </div>
      <CardContent className="px-2 pb-2 sm:px-5">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading...</p>
        ) : daily.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No spend recorded for this month.</p>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
            <LineChart accessibilityLayer data={daily} margin={{ left: 12, right: 12, top: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(v) => `$${Number(v).toFixed(0)}`}
              />
              <ChartTooltip
                cursor={{ stroke: "var(--muted-foreground)", strokeDasharray: "4 4" }}
                content={<ChartTooltipContent formatter={(v) => [`$${Number(v).toFixed(2)}`, "Spend"]} />}
              />
              <Line
                type="monotone"
                dataKey="spend"
                stroke="var(--color-spend)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
